import { tagName } from "@ember-decorators/component"
import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type ModalManager from "cc-frontend/services/modal-manager"
import { toDown, toLeft, toRight, toUp } from "ember-animated/transitions/move-over"
import _ from "lodash"

let headerNameStack: Array<string> = []
let bodyNameStack: Array<string> = []

interface Args {
  closeMenu: Function
  planbook: Planbook
  rotationCalendar: RotationCalendar
  showTemplateChooser: Function
}

@tagName("")
export default class PlanbookMenu extends Component<Args> {
  @tracked
  menuName = "PRIMARY"

  @tracked
  headerNameStack = ["planner/menus/planbook-menu/primary/title"]

  @tracked
  bodyNameStack = ["planner/menus/planbook-menu/primary/body"]

  // services
  @service modalManager!: ModalManager

  headerAnimationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal === undefined || oldVal === null) return toUp
    if (newVal === undefined || newVal === null) return toUp
    let newValInner = Object.getOwnPropertySymbols(newVal).find(
      (s) => s.toString() === "Symbol(INNER)"
    )!
    let oldValInner = Object.getOwnPropertySymbols(oldVal).find(
      (s) => s.toString() === "Symbol(INNER)"
    )!
    let newComponentName = newVal[newValInner].name
    let oldComponentName = oldVal[oldValInner].name

    // we're going backwards
    if (
      _.includes(headerNameStack, newComponentName) &&
      _.includes(headerNameStack, oldComponentName)
    ) {
      _.pull(headerNameStack, oldComponentName)
      return toDown
    } else {
      headerNameStack.push(newComponentName)
      return toUp
    }
  }

  animationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal === undefined || oldVal === null) return toLeft
    if (newVal === undefined || newVal === null) return toLeft
    let newValInner = Object.getOwnPropertySymbols(newVal).find(
      (s) => s.toString() === "Symbol(INNER)"
    )!
    let oldValInner = Object.getOwnPropertySymbols(oldVal).find(
      (s) => s.toString() === "Symbol(INNER)"
    )!
    let newComponentName = newVal[newValInner].name
    let oldComponentName = oldVal[oldValInner].name

    // For the first check, there's an odd case where the new is null and then the new is present and the old is null
    // and this just makes sure we're always going the right way if we're returning home.
    if (
      newComponentName === "planner/menus/planbook-menu/primary/body" ||
      (_.includes(bodyNameStack, newComponentName) && _.includes(bodyNameStack, oldComponentName))
    ) {
      // we're going backwards
      _.pull(bodyNameStack, oldComponentName)
      return toRight
    } else {
      bodyNameStack.push(newComponentName)
      bodyNameStack = _.uniq(bodyNameStack)
      return toLeft
    }
  }

  @action
  backToPrimaryMenu() {
    this.menuName = "PRIMARY"
  }
  @action
  goToEditTitle() {
    this.menuName = "EDIT_TITLE"
  }
  @action
  goToChangeStartOrEnd() {
    this.menuName = "CHANGE_START_OR_END"
  }
  @action
  goToAdjustWeeklyOrder() {
    this.modalManager.openModal("planner/modals/weekly-order", {
      planbook: this.args.planbook,
      rotationCalendar: this.args.rotationCalendar,
    })
    this.args.closeMenu()
  }

  @action
  goToEditPlanbook(screen) {
    this.modalManager.openModal("planner/planbook/create-edit/edit-modal", {
      planbook: this.args.planbook,
      rotationCalendar: this.args.rotationCalendar,
      screen: screen,
    })
    this.args.closeMenu()
  }

  @action
  goToAddSpecialEvents() {
    this.modalManager.openModal("planner/planbook/special-events/list-modal", {
      planbook: this.args.planbook,
      rotationCalendar: this.args.rotationCalendar,
      modalTitle: "Events & Days Off",
    })
    this.args.closeMenu()
  }

  @action
  goToConfirmMigrateToUnifiedLessons() {
    this.menuName = "CONFIRM_MIGRATE_TO_UNIFIED_LESSONS"
  }

  @action
  goToDeleteClasses() {
    this.menuName = "DELETE_CLASSES"
  }
  @action
  goToConfirmDeletePlanbook() {
    console.log("confirm delete")
    this.menuName = "CONFIRM_DELETE_PLANBOOK"
  }
}
